class HeaderNav extends HTMLElement {
  constructor() {
    super();
    this.menuItems = this.querySelectorAll(".has-dropdown");

    this.menuItems.forEach((item) => {
      const button = item.querySelector("button");
      button.addEventListener("click", (event) => this.toggle(event, item));
      item.addEventListener("keydown", (event) =>
        this.handleKeydown(event, item)
      );
      // Add focusout listener to each dropdown item
      item.addEventListener("focusout", (event) =>
        this.handleFocusOut(event, item)
      );
    });

    document.addEventListener("click", (event) =>
      this.closeOnClickOutside(event)
    );

    // Handle responsive navigation accessibility
    this.handleResponsiveNavigation();
    window.addEventListener("resize", () => this.handleResponsiveNavigation());
  }

  handleResponsiveNavigation() {
    const isDesktop = window.innerWidth > 1024;
    const nav = this.querySelector(".header__nav");
    const navLinks = this.querySelectorAll(
      ".header__nav-link, .header__dropdown-link"
    );

    if (isDesktop) {
      // On desktop: make navigation accessible
      if (nav) {
        nav.removeAttribute("aria-hidden");
        nav.removeAttribute("inert");
      }
      // Restore tab order for all navigation links
      navLinks.forEach((link) => {
        link.removeAttribute("tabindex");
      });
    } else {
      // On mobile: hide desktop navigation from assistive technologies
      if (nav) {
        nav.setAttribute("aria-hidden", "true");
        nav.setAttribute("inert", "");
      }
      // Remove from tab order
      navLinks.forEach((link) => {
        link.setAttribute("tabindex", "-1");
      });
      // Close any open dropdowns
      this.closeAll();
    }
  }

  handleFocusOut(event, item) {
    // Check if the focus is moving to an element outside the current menu item
    const isFocusOutsideItem = !item.contains(event.relatedTarget);
    if (isFocusOutsideItem) {
      this.closeMenu(item);
    }
  }

  toggle(event, item) {
    event.preventDefault();
    const wasOpen = item.classList.contains("is-open");

    // Close all menus before opening a new one
    this.closeAll();

    if (!wasOpen) {
      this.openMenu(item);
    }
  }

  handleKeydown(event, item) {
    if (event.key !== "Escape") return;

    if (item.classList.contains("is-open")) {
      event.preventDefault();
      this.closeMenu(item);
      item.querySelector("button")?.focus();
    }
  }

  openMenu(item) {
    item.classList.add("is-open");
    const button = item.querySelector("button");
    button.setAttribute("aria-expanded", "true");
  }

  closeMenu(item) {
    item.classList.remove("is-open");
    const button = item.querySelector("button");
    button.setAttribute("aria-expanded", "false");
  }

  closeAll() {
    this.menuItems.forEach((item) => {
      this.closeMenu(item);
    });
  }

  closeOnClickOutside(event) {
    if (!this.contains(event.target)) {
      this.closeAll();
    }
  }
}

customElements.define("header-nav", HeaderNav);

class MobileNavDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this.querySelector("#MobileNavDrawer");
    this.openButton = document.querySelector(".header__hamburger-button");

    if (!this.drawer || !this.openButton) return;

    this.closeButton = this.drawer.querySelector(
      ".mobile-nav-drawer__close-button"
    );

    this.setupEventListeners();
    this.setupAccordions();
    this.handleResponsiveNavigation();

    window.addEventListener("resize", () => this.handleResponsiveNavigation());
  }

  setupEventListeners() {
    // Open drawer
    this.openButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.open();
    });

    // Close drawer
    this.closeButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.close();
    });

    // Close on backdrop click
    this.drawer.addEventListener("click", (e) => {
      if (e.target === this.drawer) {
        this.close();
      }
    });

    // Handle keyboard navigation
    this.drawer.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        this.close();
      }
    });

    // Handle dialog events
    this.drawer.addEventListener("close", () => {
      this.openButton.setAttribute("aria-expanded", "false");
      this.openButton.focus(); // Return focus to trigger
    });
  }

  setupAccordions() {
    const accordions = this.drawer.querySelectorAll(".has-submenu");
    accordions.forEach((accordion) => {
      const toggle = accordion.querySelector(".mobile-nav__submenu-toggle");
      if (!toggle) return;

      toggle.addEventListener("click", () => {
        const submenu = this.drawer.querySelector(
          `#${toggle.getAttribute("aria-controls")}`
        );
        if (!submenu) return;

        const isExpanded = toggle.getAttribute("aria-expanded") === "true";
        toggle.setAttribute("aria-expanded", String(!isExpanded));
        submenu.toggleAttribute("hidden");
      });
    });
  }

  open() {
    this.drawer.showModal();
    this.openButton.setAttribute("aria-expanded", "true");

    // Let the dialog handle focus automatically, but ensure close button gets focus
    // Use requestAnimationFrame to ensure dialog is fully rendered
    requestAnimationFrame(() => {
      if (this.closeButton) {
        this.closeButton.focus();
      }
    });
  }

  close() {
    this.drawer.close();
  }

  handleResponsiveNavigation() {
    const isDesktop = window.innerWidth > 1024;

    if (isDesktop) {
      // Close drawer if open when switching to desktop
      if (this.drawer.open) {
        this.close();
      }
    }
  }
}

customElements.define("mobile-nav-drawer", MobileNavDrawer);
